import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * Componente genérico para canjear denarios filtrado por categoría.
 * Reemplaza a CanjesAcademicos y CanjesMateriales para categorías nuevas
 * (Certificados, Privilegios, Otros) y evita el bug de rutas inexistentes
 * que redirigían al Dashboard cuando la categoría no tenía ruta registrada.
 *
 * Props:
 *  - user: usuario actual
 *  - categoria: valor exacto del campo `categoria` en planes_canje (ej: "Certificados")
 *  - titulo: título mostrado en el header (ej: "Certificados")
 *  - icono: emoji para el header (ej: "🎓")
 *  - descripcion: subtítulo mostrado bajo el saldo
 *  - gradient: clases Tailwind para el gradient del header (ej: "from-purple-500 to-pink-600")
 *  - accent: clases para el badge de categoría (ej: "bg-purple-100 text-purple-700 border-purple-200")
 *  - buttonGradient: gradient para el botón "Canjear" (ej: "from-purple-500 to-pink-600")
 */
const CanjesCategoria = ({
  user,
  categoria,
  titulo,
  icono = '🎁',
  descripcion,
  gradient = 'from-indigo-500 to-purple-600',
  accent = 'bg-indigo-100 text-indigo-700 border-indigo-200',
  buttonGradient = 'from-indigo-500 to-purple-600',
}) => {
  const API = `${window.location.origin}/api`;
  const [planes, setPlanes] = useState([]);
  const [saldoActual, setSaldoActual] = useState(0);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [claseSeleccionada, setClaseSeleccionada] = useState('');
  const [mensajeAdicional, setMensajeAdicional] = useState('');
  const [clases, setClases] = useState([]);
  const [activeTab, setActiveTab] = useState('disponibles');

  // Match tolerante: normaliza a minúsculas y sin acentos para que "Académico" y "academico" caigan igual.
  const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const categoriaKey = normalize(categoria);

  useEffect(() => {
    loadPlanesDisponibles();
    loadHistorial();
  }, [categoria]); // eslint-disable-line

  const loadPlanesDisponibles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/canjes/planes-disponibles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const todos = response.data.planes || [];
      const filtrados = todos.filter((p) => normalize(p.categoria) === categoriaKey);
      setPlanes(filtrados);
      setSaldoActual(response.data.saldo_actual || 0);
    } catch (error) {
      console.error('Error loading planes:', error);
      toast.error(`Error al cargar ${titulo}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHistorial = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/canjes/mi-historial`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const todos = response.data.canjes || [];
      setHistorial(todos.filter((c) => normalize(c.plan_categoria) === categoriaKey));
    } catch (error) {
      console.error('Error loading historial:', error);
    }
  };

  const loadClases = async (planParam = null) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/clases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clasesData = Array.isArray(response.data) ? response.data : [];
      let clasesInscritas = clasesData.filter(
        (c) => c.estudiantes_inscritos && user && c.estudiantes_inscritos.includes(user.id)
      );
      const planRef = planParam || planSeleccionado;
      if (planRef?.clases_permitidas?.length > 0) {
        const permitidas = new Set(planRef.clases_permitidas);
        clasesInscritas = clasesInscritas.filter((c) => permitidas.has(c.id));
      }
      clasesInscritas.sort((a, b) =>
        (a.titulo || a.nombre || '').localeCompare(b.titulo || b.nombre || '', 'es', { sensitivity: 'base' })
      );
      setClases(clasesInscritas);
    } catch (error) {
      console.error('Error loading clases:', error);
    }
  };

  const openConfirmModal = async (plan) => {
    setPlanSeleccionado(plan);
    setClaseSeleccionada('');
    setMensajeAdicional('');
    if (plan.requiere_seleccion_clase) {
      await loadClases(plan);
    }
    setShowConfirmModal(true);
  };

  const handleCanjear = async () => {
    if (!planSeleccionado) return;
    if (planSeleccionado.requiere_seleccion_clase && !claseSeleccionada) {
      toast.error('Debes seleccionar una clase');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/canjes/canjear`,
        {
          plan_id: planSeleccionado.id,
          clase_seleccionada_id: claseSeleccionada || null,
          mensaje_estudiante: (mensajeAdicional || '').trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message, { autoClose: 5000 });
      setShowConfirmModal(false);
      setPlanSeleccionado(null);
      setClaseSeleccionada('');
      setMensajeAdicional('');
      loadPlanesDisponibles();
      loadHistorial();
    } catch (error) {
      console.error('Error canjeando:', error);
      toast.error(error.response?.data?.detail || 'Error al realizar canje');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8" data-testid={`canjes-categoria-${categoriaKey}`}>
      <div className="mb-6">
        <Link
          to="/app/mis-denarios"
          className="text-indigo-600 hover:text-indigo-800 text-sm mb-2 inline-block"
          data-testid="link-volver-mis-denarios"
        >
          ← Volver a Mis Denarios
        </Link>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center">
          <span className="text-3xl sm:text-4xl mr-3">{icono}</span>
          {titulo}
        </h2>

        <div className={`bg-gradient-to-r ${gradient} rounded-2xl p-6 shadow-lg mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium mb-1">Tu Saldo Actual</p>
              <p className="text-white text-4xl font-bold">{saldoActual} ₯</p>
            </div>
            <div className="text-6xl">🪙</div>
          </div>
        </div>

        {descripcion && <p className="text-sm sm:text-base text-gray-600">{descripcion}</p>}
      </div>

      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('disponibles')}
          data-testid="tab-disponibles"
          className={`px-4 py-2 font-semibold transition-all ${
            activeTab === 'disponibles'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🎁 Disponibles
        </button>
        <button
          onClick={() => setActiveTab('historial')}
          data-testid="tab-historial"
          className={`px-4 py-2 font-semibold transition-all ${
            activeTab === 'historial'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📜 Mi Historial ({historial.length})
        </button>
      </div>

      {activeTab === 'disponibles' && (
        <>
          {loading ? (
            <div className="text-center py-12">
              <div className="text-xl text-gray-600">Cargando planes...</div>
            </div>
          ) : planes.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-planes">
              <div className="text-6xl mb-4">{icono}</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay {titulo.toLowerCase()} disponibles
              </h3>
              <p className="text-gray-500">
                Pronto se agregarán nuevas opciones en esta categoría
              </p>
              <Link
                to="/app/mis-denarios"
                className="mt-6 inline-block px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
                data-testid="btn-volver-empty"
              >
                ← Volver a categorías
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {planes.map((plan) => (
                <div
                  key={plan.id}
                  className={`border-2 rounded-2xl p-6 transition-all hover:shadow-xl ${
                    plan.puede_canjear
                      ? 'border-indigo-200 bg-gradient-to-br from-white to-indigo-50'
                      : 'border-gray-200 bg-gray-50 opacity-75'
                  }`}
                >
                  <div className="text-center mb-4">
                    <div className="text-6xl mb-2">{plan.icono}</div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${accent}`}>
                      {plan.categoria}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">{plan.nombre}</h3>
                  <p className="text-sm text-gray-600 mb-4 text-center min-h-[40px]">{plan.descripcion}</p>
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                    <p className="text-xs text-gray-600 font-semibold mb-1">✨ Beneficio:</p>
                    <p className="text-sm text-gray-800 font-medium">{plan.beneficio}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 mb-4 shadow-inner">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Costo</p>
                      <p className="text-3xl font-bold text-indigo-600">{plan.costo_denarios} ₯</p>
                    </div>
                  </div>
                  {plan.requiere_seleccion_clase && (
                    <div className="mb-4 text-center">
                      <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                        📚 Requiere elegir clase
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => openConfirmModal(plan)}
                    disabled={!plan.puede_canjear}
                    data-testid={`btn-canjear-${plan.id}`}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      plan.puede_canjear
                        ? `bg-gradient-to-r ${buttonGradient} text-white hover:shadow-lg hover:scale-105`
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {plan.puede_canjear ? '🎉 Canjear Ahora' : '❌ Saldo Insuficiente'}
                  </button>
                  {!plan.puede_canjear && (
                    <p className="text-xs text-center text-red-600 mt-2">
                      Te faltan {plan.costo_denarios - saldoActual} ₯
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'historial' && (
        <>
          {historial.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📜</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Sin canjes realizados
              </h3>
              <p className="text-gray-500">Tus canjes aparecerán aquí</p>
            </div>
          ) : (
            <div className="space-y-4">
              {historial.map((canje) => (
                <div
                  key={canje.id}
                  className="border-2 border-indigo-200 rounded-xl p-6 hover:shadow-md transition-all bg-indigo-50"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-1">{canje.plan_nombre}</h4>
                      <p className="text-sm text-gray-600 mb-2">{canje.plan_beneficio}</p>
                      {canje.clase_seleccionada_nombre && (
                        <p className="text-sm text-indigo-600">📚 Clase: {canje.clase_seleccionada_nombre}</p>
                      )}
                      {canje.mensaje_estudiante && (
                        <div className="mt-2 bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded">
                          <p className="text-xs text-yellow-900 italic">
                            <strong>💬 Tu mensaje:</strong> {canje.mensaje_estudiante}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600 mb-1">-{canje.costo_denarios} ₯</p>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        {canje.estado}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">Canjeado el {formatFecha(canje.fecha_canje)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showConfirmModal && planSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className={`bg-gradient-to-r ${gradient} p-6 rounded-t-2xl`}>
              <h3 className="text-2xl font-bold text-white text-center">🎁 Confirmar Canje</h3>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{planSeleccionado.icono}</div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{planSeleccionado.nombre}</h4>
                <p className="text-sm text-gray-600 mb-4">{planSeleccionado.descripcion}</p>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-800 font-medium">✨ {planSeleccionado.beneficio}</p>
                </div>
              </div>

              {planSeleccionado.requiere_seleccion_clase && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Selecciona la clase *</label>
                  <select
                    value={claseSeleccionada}
                    onChange={(e) => setClaseSeleccionada(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                    required
                  >
                    <option value="">-- Elige una clase --</option>
                    {clases.map((clase) => (
                      <option key={clase.id} value={clase.id}>
                        {clase.titulo}
                      </option>
                    ))}
                  </select>
                  {clases.length === 0 && (
                    <p className="text-xs text-red-600 mt-2">
                      No estás inscrito en ninguna clase
                      {planSeleccionado.clases_permitidas?.length > 0 ? ' habilitada para este canje' : ''}
                    </p>
                  )}
                </div>
              )}

              {planSeleccionado.requiere_seleccion_clase && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">
                    Mensaje al profesor <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                  </label>
                  <textarea
                    data-testid="textarea-mensaje-canje"
                    value={mensajeAdicional}
                    onChange={(e) => setMensajeAdicional(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">{mensajeAdicional.length}/500 caracteres</p>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Costo del canje:</span>
                  <span className="text-xl font-bold text-red-600">-{planSeleccionado.costo_denarios} ₯</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Saldo actual:</span>
                  <span className="text-xl font-bold text-gray-800">{saldoActual} ₯</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800 font-semibold">Saldo después:</span>
                    <span className="text-2xl font-bold text-green-600">
                      {saldoActual - planSeleccionado.costo_denarios} ₯
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPlanSeleccionado(null);
                    setClaseSeleccionada('');
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCanjear}
                  data-testid="btn-confirmar-canje"
                  className={`flex-1 px-6 py-3 bg-gradient-to-r ${buttonGradient} text-white rounded-xl font-semibold hover:shadow-lg transition-all`}
                >
                  Confirmar Canje
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CanjesCategoria;
